import React, { useState, useEffect, useCallback, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import TreeView from '../components/TreeView';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import api from '../services/api';
import './Dashboard.css';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = () => {
  const { user } = useAuth();
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [dadosGrafico, setDadosGrafico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [error, setError] = useState('');
  const [alertas, setAlertas] = useState([]);
  const [totalAlertas, setTotalAlertas] = useState(0);
  const [loadingAlertas, setLoadingAlertas] = useState(false);

  const isAdmin = user?.tipo_usuario === 'admin';
  const isGestor = user?.tipo_usuario === 'gestor';
  const podeVerDashboard = isAdmin || isGestor;

  useEffect(() => {
    if (podeVerDashboard && user) {
      carregarEstatisticas();
    } else if (!podeVerDashboard) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVerDashboard, ano]);

  // Carregar alertas para todos os usuários
  useEffect(() => {
    if (user) {
      carregarAlertas();
      // Atualizar alertas a cada 5 minutos
      const interval = setInterval(carregarAlertas, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSelectDocumento = useCallback(async (data) => {
    try {
      // Verificar se tem id_protocolo
      if (!data || !data.id_protocolo) {
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.error('Documento sem id_protocolo:', data);
        }
        setSelectedDocumento(data);
        return;
      }

      // Buscar detalhes completos do protocolo
      const response = await api.get(`/protocolos/${data.id_protocolo}`);
      if (response.data && response.data.success) {
        setSelectedDocumento(response.data.data);
        setError(''); // Limpar erros
      } else {
        const errorMsg = response.data?.message || 'Erro ao carregar documento';
        setError(errorMsg);
        setSelectedDocumento(data); // Usar dados básicos como fallback
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Erro ao carregar documento';
      setError(errorMsg);
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro completo ao carregar documento:', {
          error: err,
          response: err.response?.data,
          data: data
        });
      }
      setSelectedDocumento(data); // Usar dados básicos como fallback
    }
  }, []);

  const handleClearFilter = useCallback(() => {
    setSelectedDocumento(null);
  }, []);

  const carregarEstatisticas = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/estatisticas/movimentos-secretarias?ano=${ano}`);
      
      if (response.data && response.data.success) {
        const dados = response.data.data || [];
        setDadosGrafico(dados);
        if (dados.length === 0) {
          setError('Nenhum dado disponível para o período selecionado.');
        }
      } else {
        setError(response.data?.message || 'Erro ao carregar estatísticas');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Erro ao carregar dados do gráfico';
      setError(errorMsg);
      setDadosGrafico(null);
    } finally {
      setLoading(false);
    }
  };

  const carregarAlertas = async () => {
    try {
      setLoadingAlertas(true);
      const [contadorRes, alertasRes] = await Promise.all([
        api.get('/alertas/contador'),
        api.get('/alertas?visualizado=false&limit=10')
      ]);

      if (contadorRes.data.success) {
        setTotalAlertas(contadorRes.data.data.total || 0);
      }

      if (alertasRes.data.success) {
        setAlertas(alertasRes.data.data || []);
      }
    } catch (err) {
      // Ignorar erros silenciosamente
    } finally {
      setLoadingAlertas(false);
    }
  };

  const marcarAlertaVisualizado = async (idAlerta) => {
    try {
      await api.post(`/alertas/${idAlerta}/visualizar`);
      await carregarAlertas();
    } catch (err) {
      // Ignorar erro
    }
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Preparar dados para o gráfico
  const prepararDadosGrafico = () => {
    if (!dadosGrafico || dadosGrafico.length === 0) {
      return null;
    }

    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    
    const datasets = dadosGrafico.map((secretaria, index) => {
      // Gerar cores diferentes para cada secretaria
      const cores = [
        'rgba(59, 130, 246, 0.8)',   // Azul
        'rgba(16, 185, 129, 0.8)',   // Verde
        'rgba(245, 101, 101, 0.8)',  // Vermelho
        'rgba(251, 191, 36, 0.8)',   // Amarelo
        'rgba(139, 92, 246, 0.8)',   // Roxo
        'rgba(236, 72, 153, 0.8)',   // Rosa
        'rgba(20, 184, 166, 0.8)',   // Ciano
        'rgba(249, 115, 22, 0.8)',   // Laranja
      ];
      
      const cor = cores[index % cores.length];
      const corBorda = cor.replace('0.8', '1');

      return {
        label: `${secretaria.sigla} - ${secretaria.descricao}`,
        data: secretaria.dados.map(d => d.quantidade),
        borderColor: corBorda,
        backgroundColor: cor,
        borderWidth: 2,
        fill: false,
        tension: 0.4
      };
    });

    return {
      labels: meses,
      datasets: datasets
    };
  };

  const opcoesGrafico = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: `Movimentos por Secretaria - ${ano}`,
        font: {
          size: 18,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 30
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0
        },
        title: {
          display: true,
          text: 'Quantidade de Movimentos'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Meses do Ano'
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const dadosChart = prepararDadosGrafico();

  return (
    <div className="dashboard-container">
      <div className="dashboard-layout">
        <aside className="dashboard-sidebar">
          <TreeView onSelectDocumento={handleSelectDocumento} />
        </aside>
        <main className="dashboard-main">
          <div className="dashboard-header">
            <h1>
              Dashboard - Movimentos por Secretaria
              {isGestor && user?.secretaria && (
                <span className="dashboard-secretaria-label">
                  {' '}({user.secretaria.sigla})
                </span>
              )}
            </h1>
            {selectedDocumento && (
              <div className="dashboard-selected">
                <span className="selected-label">Documento selecionado:</span>
                <span className="selected-value">
                  {selectedDocumento.numero_protocolo || selectedDocumento.assunto || 'Documento'}
                </span>
                <button 
                  className="btn-clear-filter"
                  onClick={handleClearFilter}
                >
                  Limpar
                </button>
              </div>
            )}
            {!selectedDocumento && podeVerDashboard && (
              <div className="dashboard-ano-selector">
                <label htmlFor="ano-select">Ano: </label>
                <select
                  id="ano-select"
                  value={ano}
                  onChange={(e) => setAno(parseInt(e.target.value))}
                  className="ano-select"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const anoOption = new Date().getFullYear() - i;
                    return (
                      <option key={anoOption} value={anoOption}>
                        {anoOption}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          <div className="dashboard-content">
            {/* Seção de Alertas */}
            {totalAlertas > 0 && (
              <div className="dashboard-card alertas-card">
                <div className="alertas-header">
                  <h2>⚠️ Alertas de Prazo ({totalAlertas})</h2>
                  <button 
                    className="btn-refresh-alertas"
                    onClick={carregarAlertas}
                    disabled={loadingAlertas}
                  >
                    🔄 Atualizar
                  </button>
                </div>
                <div className="alertas-list">
                  {alertas.slice(0, 5).map((alerta) => (
                    <div 
                      key={alerta.id_alerta} 
                      className={`alerta-item alerta-${alerta.tipo_alerta}`}
                      onClick={() => marcarAlertaVisualizado(alerta.id_alerta)}
                    >
                      <div className="alerta-icon">
                        {alerta.tipo_alerta === 'vencido' && '🔴'}
                        {alerta.tipo_alerta === 'vencendo' && '🟠'}
                        {alerta.tipo_alerta === 'atencao' && '🟡'}
                      </div>
                      <div className="alerta-content">
                        <div className="alerta-protocolo">
                          {alerta.protocolo?.numero_protocolo || 'N/A'}
                        </div>
                        <div className="alerta-mensagem">
                          {alerta.mensagem || alerta.protocolo?.assunto || 'Alerta de prazo'}
                        </div>
                        <div className="alerta-info">
                          {alerta.dias_restantes !== null && (
                            <span>
                              {alerta.dias_restantes < 0 
                                ? `Vencido há ${Math.abs(alerta.dias_restantes)} dia(s)`
                                : `${alerta.dias_restantes} dia(s) restante(s)`
                              }
                            </span>
                          )}
                          <span>Vence em: {formatarData(alerta.data_vencimento)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {alertas.length > 5 && (
                    <div className="alerta-more">
                      <a href="/documentos" style={{ color: '#F97316', textDecoration: 'none' }}>
                        Ver todos os {totalAlertas} alertas →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="dashboard-card" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>⚠️ {error}</span>
                  <button 
                    onClick={() => setError('')} 
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#DC2626' }}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            {!podeVerDashboard ? (
              <div className="dashboard-card">
                <p>Acesso restrito. Apenas administradores e gestores podem visualizar o dashboard.</p>
              </div>
            ) : selectedDocumento ? (
              <div className="dashboard-card documento-detalhes">
                <div className="documento-header">
                  <h2>{selectedDocumento.assunto || 'Documento'}</h2>
                  <button className="btn-fechar-doc" onClick={handleClearFilter}>×</button>
                </div>
                <div className="documento-info-dashboard">
                  <div className="info-row">
                    <strong>Número:</strong> <span>{selectedDocumento.numero_protocolo || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <strong>Tipo:</strong> <span>{selectedDocumento.tipo_documento || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <strong>Origem:</strong> <span>{selectedDocumento.setorOrigem?.nome_setor || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <strong>Destino:</strong> <span>{selectedDocumento.setorDestino?.nome_setor || 'N/A'}</span>
                  </div>
                  {selectedDocumento.conteudo && (
                    <div className="info-row full-width">
                      <strong>Conteúdo:</strong>
                      <div className="conteudo-box">{selectedDocumento.conteudo}</div>
                    </div>
                  )}
                  {selectedDocumento.anexos && selectedDocumento.anexos.length > 0 && (
                    <div className="info-row full-width">
                      <strong>Anexos ({selectedDocumento.anexos.length}):</strong>
                      <div className="anexos-list-dashboard">
                        {selectedDocumento.anexos.map((anexo) => (
                          <div key={anexo.id_anexo} className="anexo-item-dashboard">
                            <span>📎 {anexo.nome_original}</span>
                            <a
                              href="#"
                              className="btn-download"
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  // Fazer download via API com autenticação
                                  const response = await api.get(`/protocolos/anexos/${anexo.id_anexo}/download`, {
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', anexo.nome_original);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  // Log apenas em desenvolvimento
                                  if (process.env.NODE_ENV === 'development') {
                                    console.error('Erro ao baixar anexo:', err);
                                  }
                                }
                              }}
                            >
                              📥 Baixar
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : loading ? (
              <div className="dashboard-card">
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Carregando dados do gráfico...</p>
                </div>
              </div>
            ) : error ? (
              <div className="dashboard-card">
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p className="error-message">{error}</p>
                  <button 
                    onClick={carregarEstatisticas}
                    style={{
                      marginTop: '1rem',
                      padding: '0.5rem 1rem',
                      background: '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 0,
                      cursor: 'pointer'
                    }}
                  >
                    Tentar Novamente
                  </button>
                </div>
              </div>
            ) : dadosChart && dadosChart.datasets && dadosChart.datasets.length > 0 ? (
              <div className="dashboard-card dashboard-chart-card">
                <div className="chart-container">
                  <Line data={dadosChart} options={opcoesGrafico} />
                </div>
              </div>
            ) : (
              <div className="dashboard-card">
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Nenhum dado disponível para o período selecionado.</p>
                  <p style={{ color: '#6B7280', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    Não há movimentos registrados para as secretarias no ano {ano}.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default memo(Dashboard);
